import styled from "styled-components";
import { Fixture } from "../../../models/fixture";
import { FC, useState } from "react";
import placeholderImage from "../../../images/common/vgc-logo.png"
import { Team } from "../../../models/team";
import ReactModal from "../../common/ReactModal";
import ScoreCard from "./ScoreCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGavel } from "@fortawesome/free-solid-svg-icons";

interface Props {
  fixture: Fixture
  team: Team
}

const FixtureCard: FC<Props> = ({ fixture, team }) => {
  // Functions
  function calculateResult(fixture: Fixture, team: Team): 'win' | 'loss' | 'draw' | 'disputed' | 'notPlayed' {
    if (fixture.isDisputed) {
      return 'disputed';
    }
    if (!fixture.isPlayed) {
      return 'notPlayed';
    }

    let isTeamHome = fixture.home === team.name;
    let isTeamAway = fixture.away === team.name;

    if (isTeamHome) {
      if (fixture.homeScore > fixture.awayScore) {
        return 'win';
      } else if (fixture.homeScore < fixture.awayScore) {
        return 'loss';
      } else {
        return 'draw';
      }
    } else if (isTeamAway) {
      if (fixture.awayScore > fixture.homeScore) {
        return 'win';
      } else if (fixture.awayScore < fixture.homeScore) {
        return 'loss';
      } else {
        return 'draw';
      }
    }
    // In case team is not part of the fixture
    return 'draw';
  }

  function formatDate(fixture: Fixture): { dateString: string; extendedMinutes: number } {
    return {
      dateString: `Week ${fixture.week}\n${fixture.date}\n${fixture.time}`,
      extendedMinutes: fixture.minutesExtended,
    };
  }

  // Data
  const result = calculateResult(fixture, team);
  const date = formatDate(fixture);

  // Methods
  const handleClickOpenScoreCard = () => {
    setShowScoreCardModal(true)
  }

  const goToLiveLink = (liveLink: string) => {
    window.open(liveLink, '_blank');
  };

  // Modals
  const [showScoreCardModal, setShowScoreCardModal] = useState<boolean>(false);

  const closeModals = () => {
    setShowScoreCardModal(false)
  };
  return (
    <>
      <Wrapper>
        <Container >
        <FixturesContainer
          result={result}
          className="gradient"
        >
          <BadgeContainer>
          <Badge image={fixture.homeImage || placeholderImage}></Badge>
            <BadgeText> {fixture.isDisputed && <DisputedIcon icon={faGavel} />}{fixture.home}</BadgeText>
          </BadgeContainer>
          <MiddleContainer>
            <TextContainer>
                <DateContainer className="heading">
                  <span>
                    {date.dateString}
                    {date.extendedMinutes > 0 && (
                      <> {" "}
                        <ExtendedMinutes className="text-bold">+{date.extendedMinutes}'</ExtendedMinutes>
                      </>
                    )}
                  </span>
          </DateContainer>
              <Button
                type="button"
                className="btn btn-danger btn-sm px-1"
                disabled={!team.liveLink}
                onClick={() => team.liveLink && goToLiveLink(team.liveLink)}
              >
                WATCH
                <LiveButton> LIVE</LiveButton>
              </Button>
            </TextContainer>
          </MiddleContainer>
          <BadgeContainer>
            <Badge image={fixture.awayImage || placeholderImage}></Badge>
            <BadgeText className=""> {fixture.isDisputed && <DisputedIcon icon={faGavel} />}{fixture.away}</BadgeText>
          </BadgeContainer>
        </FixturesContainer>

        <StatsContainer>
          <ButtonContainer>
              <StatsButton
                onClick={handleClickOpenScoreCard}
                className={`btn btn-sm btn-dark`}
                disabled={fixture.isLocked && !fixture.isPlayed}
              >
              SCORE<br></br> CARD
            </StatsButton>
          </ButtonContainer>
        </StatsContainer>
      </Container>
      </Wrapper>
      
      {/* Score Card Modal */}
      {fixture ?
        <ReactModal 
          modalOpen={showScoreCardModal} 
          closeModal={() => closeModals()}
          component={<ScoreCard fixtureId={fixture._id} team={team}/>}
        />
      : null}
    </>
  );
};

export default FixtureCard;

const Wrapper = styled.div`
  padding: 0.5rem 0;
  //font-size: 12px;
  @media (max-width: 480px) {
    font-size: 10px;
  }
  @media (max-width: 567px) {
    padding: 0.3rem 0;
  }
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 0.7rem;
  
`;
const Container = styled.div`
  position: relative;
  background: linear-gradient(120deg, #000000ff 60%, #040a2cff 100%);
  display: flex;
  border-radius: 14px;

  max-width: 400px;
  min-width: 250px;
  width: 100%;

  border: 2px solid #5fa8e6;
  box-shadow: 0 0 8px 1px #00eaff;
`;
const BadgeContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  width: 28%;
  padding: 0.2rem;
  aspect-ratio: 1/1;
  /* background-color: gray; */

  
`;
interface BadgeProps {
  image: string;
}

const Badge = styled.div<BadgeProps>`
  max-height: 50px;
  height: 100%;
  aspect-ratio: 1/1;
  background-image: url(${(props) => props.image});
  background-size: cover;
  border-radius: 8px;
  @media (max-width: 350px) {
    max-height: 50px;
  }
`;

const BadgeText = styled.div`
  text-align: center;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 2em;
  
`;
const MiddleContainer = styled.div`
  width: 44%;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 0.3rem;
  @media (max-width: 1200px) {
    /* font-size: smaller; */
  }
  @media (max-width: 480px) {
    /* font-size: small; */
  }
`;
const TextContainer = styled.div`
  max-width: 100px;
  text-align: center;
  color: white;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
`;
const DateContainer = styled.div`
  /* font-size: smaller; */
  margin: 0;
  padding: 0;

  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;
const VsText = styled.h4`
  margin: 0;
  padding: 0;
`;
interface FixturesContainerProps {
  result: 'win' | 'loss' | 'draw' | 'notPlayed' | 'disputed';
}

const FixturesContainer = styled.div<FixturesContainerProps>`
  padding: 0.3rem;
  display: flex;
  width: 100%;
  border-bottom-left-radius: 12px;
  border-top-left-radius: 12px;

  background: ${(props) => {
    switch (props.result) {
      case "win":
        return "linear-gradient(120deg, rgba(3, 236, 119, 0.43) 0%, rgba(0, 0, 0, 0.9) 100%)";
      case "loss":
        return "linear-gradient(120deg, rgba(186, 25, 25, 0.49) 0%, rgba(0, 0, 0, 0.9) 100%)";
      case "draw":
        return "linear-gradient(120deg, rgba(200, 180, 0, 0.54) 0%, rgba(0, 0, 0, 0.9) 100%)";
      case "disputed":
        return "linear-gradient(120deg, rgba(100, 100, 100, 0.2) 0%, rgba(0, 0, 0, 0.9) 100%)";
      case "notPlayed":
        return "linear-gradient(120deg, rgba(100, 100, 100, 0.2) 0%, rgba(0, 0, 0, 0.9) 100%)";
      default:
        return "linear-gradient(120deg, rgba(100, 100, 100, 0.2) 0%, rgba(0, 0, 0, 0.9) 100%)";
    }
  }};
`;
const StatsContainer = styled.div`
  width: 20%;
  padding: 0.05rem;
`;
const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;
const StatsButton = styled.button`
  width: 100%;
  margin: 0;
  padding: 0;
  height: 100%;
  border-bottom-left-radius: 0px;
  border-top-left-radius: 0px;
  border-bottom-right-radius: 14px;
  border-top-right-radius: 14px;
  cursor: pointer;
  font-size: 12px;
  background: linear-gradient(120deg, #000000ff 60%, #1a1c28 100%);
  border: 2px solid #5fa8e6;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
  &:hover:not(:disabled) {
    box-shadow: 0 0 8px 1px #00eaff;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const Button = styled.button`
  padding: 0.1rem;
  @media (max-width: 567px) {
    /* margin: 0 0.5rem;
    padding: 0.2rem 0.6rem; */
  }
  @media (min-width: 991px) {
    /* font-size: smaller; */
    font-size: 12px;
    @media (max-width: 480px) {
      font-size: 10px;
    }
  }
`;
const LiveButton = styled.span`
  @media (max-width: 567px) {
    /* display: none; */
  }
`;

const StrikeThrough = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background-color: red;
  z-index: 2;
`;

const DisputedBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 5px 10px;
  border-radius: 5px;
  font-weight: bold;
  z-index: 2;
`;
const DisputedBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: red;
  color: white;
  text-align: center;
  padding: 5px;
  font-size: 14px;
  z-index: 2;
`;
const DisputedIcon = styled(FontAwesomeIcon)`
  margin-right: 5px;
`;
const ExtendedMinutes = styled.span`
  font-weight: bold;
`;
